// Camera component for the staff stamping flow. Mirrors the owner
// QrScannerTab (camera stays live across scans, decode pauses while overlay
// is open, torch + beep + vibrate feedback) but instead of looking up the
// card it just hands the parent the raw scanned token and the parsed
// cardId — the staff stamp endpoint takes the raw token in the body and
// verifies the HMAC server-side.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'

import { decodeStampToken } from '../../shared/stamp/decodeStampToken.js'
import { TorchIcon, SoundOnIcon, SoundOffIcon } from '../../owner/components/icons.js'
import {
  isStampSoundEnabled,
  setStampSoundEnabled,
} from '../../shared/sound/stampSound.js'

export interface ScannedToken {
  cardId: string
  rawToken: string
}

interface Props {
  onScanned: (s: ScannedToken) => void
  paused?: boolean
}

function playBeep() {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12)
    osc.connect(gain).connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.13)
    osc.onended = () => ctx.close().catch(() => {})
  } catch {
    // non-essential
  }
}

export function StaffScanner({ onScanned, paused = false }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const pausedRef = useRef(paused)
  const [error, setError] = useState<string | null>(null)
  const [torchOn, setTorchOn] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)
  const [soundOn, setSoundOn] = useState(isStampSoundEnabled)
  // True from the moment a valid QR is decoded until the parent navigates to
  // the confirm step. Drives a persistent "Scanned" cover (not a brief flash)
  // so staff stop aiming at the pass — the beep/vibrate alone weren't enough.
  const [captured, setCaptured] = useState(false)

  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  // Drop the cover when scanning resumes (a consumer toggles `paused` off after
  // closing its overlay). On StaffScanPage `paused` is always false and the
  // page navigates away on a scan, so the cover persists until unmount.
  useEffect(() => {
    if (!paused) setCaptured(false)
  }, [paused])

  const onScannedRef = useRef(onScanned)
  useEffect(() => {
    onScannedRef.current = onScanned
  }, [onScanned])

  useEffect(() => {
    if (!containerRef.current) return
    const containerId = 'staff-qr-reader'
    containerRef.current.id = containerId
    const scanner = new Html5Qrcode(containerId, { verbose: false })

    let running = false
    let cancelled = false
    let lastDecode = { value: '', at: 0 }

    // Fully release the camera on teardown. See QrScannerTab for why
    // scanner.stop() alone isn't enough — some Android/Chrome builds keep
    // the video tracks (and the OS camera indicator) live after stop().
    const releaseCamera = () => {
      try {
        const videoEl = containerRef.current?.querySelector('video')
        const stream = (
          videoEl as (HTMLVideoElement & { srcObject?: MediaStream | null }) | null
        )?.srcObject
        stream?.getTracks?.().forEach((t) => {
          try {
            t.stop()
          } catch {
            // track already ended — benign
          }
        })
      } catch {
        // probe failure — benign
      }
    }

    const safeStop = async () => {
      if (!running) {
        releaseCamera()
        return
      }
      running = false
      try {
        await scanner.stop()
      } catch {
        // benign
      }
      releaseCamera()
      try {
        scanner.clear()
      } catch {
        // benign
      }
    }

    const handleSuccess = (decoded: string) => {
      if (cancelled || pausedRef.current) return
      const value = decoded.trim()
      const now = Date.now()
      if (value === lastDecode.value && now - lastDecode.at < 1500) return
      lastDecode = { value, at: now }
      const parsed = decodeStampToken(value)
      if (!parsed) {
        setError("That QR doesn't look like a Naya pass.")
        return
      }
      setError(null)
      try {
        if (typeof navigator.vibrate === 'function') navigator.vibrate(40)
      } catch {
        // non-essential
      }
      playBeep()
      setCaptured(true)
      onScannedRef.current({ cardId: parsed.cardId, rawToken: value })
    }

    scanner
      .start(
        { facingMode: 'environment' },
        {
          // Faster + looser scan envelope. See QrScannerTab for rationale.
          fps: 25,
          qrbox: (vw: number, vh: number) => {
            const side = Math.floor(Math.min(vw, vh) * 0.55)
            return { width: side, height: side }
          },
        },
        handleSuccess,
        () => {},
      )
      .then(() => {
        if (cancelled) {
          void safeStop()
          return
        }
        running = true
        try {
          const videoEl = containerRef.current?.querySelector('video')
          const track = (
            videoEl as HTMLVideoElement & {
              srcObject?: MediaStream | null
            }
          )?.srcObject?.getVideoTracks?.()[0]
          const caps = track?.getCapabilities?.() as
            | (MediaTrackCapabilities & { torch?: boolean })
            | undefined
          if (caps?.torch) setTorchSupported(true)
        } catch {
          // capability probe — silent
        }
      })
      .catch((err) => {
        setError(
          err instanceof Error
            ? `Camera unavailable: ${err.message}`
            : 'Camera unavailable',
        )
      })

    return () => {
      cancelled = true
      void safeStop()
    }
  }, [])

  const toggleTorch = useCallback(async () => {
    try {
      const videoEl = containerRef.current?.querySelector('video')
      const track = (
        videoEl as HTMLVideoElement & {
          srcObject?: MediaStream | null
        }
      )?.srcObject?.getVideoTracks?.()[0]
      if (!track) return
      const next = !torchOn
      await track.applyConstraints({
        advanced: [
          { torch: next } as MediaTrackConstraintSet & { torch: boolean },
        ],
      })
      setTorchOn(next)
    } catch {
      setTorchSupported(false)
    }
  }, [torchOn])

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev
      setStampSoundEnabled(next)
      return next
    })
  }, [])

  return (
    <div className="stamp-scanner">
      <div className="stamp-scanner-frame">
        <div ref={containerRef} className="stamp-scanner-viewport" />
        <button
          type="button"
          className={`stamp-sound-toggle${soundOn ? '' : ' is-muted'}`}
          onClick={toggleSound}
          aria-label={soundOn ? 'Mute stamp sound' : 'Unmute stamp sound'}
          aria-pressed={!soundOn}
        >
          {soundOn ? <SoundOnIcon /> : <SoundOffIcon />}
        </button>
        {torchSupported && (
          <button
            type="button"
            className={`stamp-torch${torchOn ? ' is-on' : ''}`}
            onClick={toggleTorch}
            aria-label={torchOn ? 'Turn off flashlight' : 'Turn on flashlight'}
            aria-pressed={torchOn}
          >
            <TorchIcon />
          </button>
        )}
        {paused && <div className="stamp-scanner-veil" aria-hidden="true" />}
        {captured && (
          <div className="stamp-scanner-captured" aria-hidden="true">
            <span className="stamp-scanner-captured-check">✓</span>
            <span className="stamp-scanner-captured-label">Scanned</span>
          </div>
        )}
      </div>
      {error && <p className="stamp-error">{error}</p>}
      {!error && (
        <p className="stamp-hint">
          Aim at the QR on the customer's pass. One tap to confirm the stamp.
        </p>
      )}
    </div>
  )
}
